/**
 * TaskTray (CABINET) — COPILOT_MASTER_PLAN Phase 1 / subsystem E + I (UI).
 *
 * Cabinet mirror of the client's TaskTray. The Companion window's "Tâches"
 * tab: lists the user's Companion/agentic tasks with a status chip, progress
 * bar, credit cost and relative time; actions = open result / cancel. Data
 * comes from the feature-detected tray API (falls back to GET /agentic/tasks/,
 * degrades to a friendly empty state — see copilot-tasks.service).
 *
 * `useCompanionTasks` also drives the badge on the minimized pill: any task
 * that transitions to a terminal state (or waits on HITL) while the tray is
 * not visible bumps the store badge.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ListTodo,
  Loader2,
  XCircle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Coins,
  ExternalLink,
  CircleSlash,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import {
  fetchCompanionTasks,
  cancelCompanionTask,
  subscribeCompanionTaskEvents,
  type CompanionTask,
  type CompanionTaskStatus,
  type CompanionTasksResult,
} from '@/services/copilot-tasks.service'
import { useCompanionWindowStore } from '@/stores/useCompanionWindowStore'

// ── Poll + badge hook (mounted once in GrcCopilotSidebar) ────────────────────

const COMPANION_TASKS_KEY = ['companion-tasks-cabinet'] as const
const TERMINAL: CompanionTaskStatus[] = ['DONE', 'FAILED', 'TIMEOUT', 'CANCELLED']
const ACTIVE: CompanionTaskStatus[] = ['QUEUED', 'RUNNING', 'HITL']

export function useCompanionTasks(enabled: boolean) {
  const bumpBadgeCount = useCompanionWindowStore((s) => s.bumpBadgeCount)
  const setBadge = useCompanionWindowStore((s) => s.setBadge)
  const queryClient = useQueryClient()

  const query = useQuery<CompanionTasksResult>({
    queryKey: COMPANION_TASKS_KEY,
    queryFn: fetchCompanionTasks,
    enabled,
    retry: false,
    staleTime: 5_000,
    // Poll every 15s — the WS subscription below adds INSTANT refreshes when
    // the notification proxy is live, but the 15s poll always stays as the
    // guaranteed fallback (coordinator contract). Back off to 60s only when
    // no tray backend has landed at all.
    refetchInterval: (q) => (q.state.data?.source === 'none' ? 60_000 : 15_000),
    refetchIntervalInBackground: false,
  })

  // WS subscription (task.created|progress|hitl_pending|completed|failed|
  // cancelled) → immediate refetch. One connection attempt per session; if the
  // gateway WS proxy isn't deployed the service marks realtime 'unavailable'
  // and the polling interval above carries the tray.
  useEffect(() => {
    if (!enabled) return
    const unsubscribe = subscribeCompanionTaskEvents(() => {
      queryClient.invalidateQueries({ queryKey: COMPANION_TASKS_KEY })
    })
    return unsubscribe
  }, [enabled, queryClient])

  // Terminal-transition + HITL detection → pill/tab badge.
  const prevStatusRef = useRef<Map<string, CompanionTaskStatus>>(new Map())
  const initializedRef = useRef(false)
  const data = query.data
  useEffect(() => {
    if (!data) return
    const prev = prevStatusRef.current
    const firstLoad = !initializedRef.current
    let completions = 0
    let hitlPending = false
    for (const t of data.tasks) {
      const was = prev.get(t.id)
      if (was && ACTIVE.includes(was) && TERMINAL.includes(t.status)) {
        completions += 1
      } else if (!was && !firstLoad && TERMINAL.includes(t.status)) {
        // Fast task: created AND completed between two refreshes — its first
        // sighting is already terminal, still counts as a completion.
        completions += 1
      }
      if (t.status === 'HITL') hitlPending = true
    }
    prevStatusRef.current = new Map(data.tasks.map((t) => [t.id, t.status]))
    initializedRef.current = true

    const { open, minimized, activeTab } = useCompanionWindowStore.getState()
    const trayVisible = open && !minimized && activeTab === 'tasks'
    if (trayVisible) return
    if (completions > 0) bumpBadgeCount(completions)
    if (hitlPending) setBadge('hitl')
  }, [data, bumpBadgeCount, setBadge])

  return query
}

// ── Presentation helpers ──────────────────────────────────────────────────────

const STATUS_META: Record<CompanionTaskStatus, { label: string; className: string }> = {
  QUEUED: { label: 'En file', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  RUNNING: { label: 'En cours', className: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300' },
  HITL: { label: 'Validation requise', className: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300' },
  DONE: { label: 'Terminée', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' },
  FAILED: { label: 'Échec', className: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' },
  CANCELLED: { label: 'Annulée', className: 'bg-muted text-muted-foreground' },
  TIMEOUT: { label: 'Expirée', className: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' },
}

/** "il y a 3 min" style relative time (fr). */
export function relativeTimeFr(iso?: string): string {
  if (!iso) return ''
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return ''
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (secs < 60) return "à l'instant"
  const mins = Math.round(secs / 60)
  if (mins < 60) return `il y a ${mins} min`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `il y a ${hours} h`
  const days = Math.round(hours / 24)
  return `il y a ${days} j`
}

function ProgressBar({ task }: { task: CompanionTask }) {
  const running = task.status === 'RUNNING' || task.status === 'QUEUED' || task.status === 'HITL'
  if (!running && task.progressPct == null) return null
  const pct = task.progressPct
  return (
    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted" data-copilot-id="task-progress">
      {pct != null ? (
        <div
          className={cn(
            'h-full rounded-full transition-all',
            task.status === 'FAILED' || task.status === 'TIMEOUT' ? 'bg-red-500' : 'bg-primary',
          )}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      ) : (
        // Indeterminate — durable tasks without progress_pct
        <div className="h-full w-1/3 animate-pulse rounded-full bg-primary/60" />
      )}
    </div>
  )
}

// ── Row ───────────────────────────────────────────────────────────────────────

function TaskRow({ task, onCancelled }: { task: CompanionTask; onCancelled: () => void }) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [cancelFailed, setCancelFailed] = useState(false)
  const meta = STATUS_META[task.status]

  const handleCancel = useCallback(async () => {
    setCancelling(true)
    setCancelFailed(false)
    const ok = await cancelCompanionTask(task)
    setCancelling(false)
    if (ok) onCancelled()
    else setCancelFailed(true)
  }, [task, onCancelled])

  // "Open result" — navigate when the result carries a route (result_ref as an
  // in-app path, or an object with a route field), else expand inline.
  const resultRoute = (() => {
    const rs = task.resultSummary
    if (typeof rs === 'string' && rs.startsWith('/')) return rs
    if (rs && typeof rs === 'object' && typeof (rs as Record<string, unknown>).route === 'string') {
      return (rs as Record<string, unknown>).route as string
    }
    return null
  })()

  const hasResultDetail = task.resultSummary != null || task.error != null

  return (
    <div
      className="rounded-lg border bg-card p-2.5 text-card-foreground"
      data-copilot-id="task-row"
      data-task-status={task.status}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
          aria-label={expanded ? 'Replier' : 'Détails'}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium" title={task.goal ?? task.title}>
            {task.title}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span
              className={cn('inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium', meta.className)}
              data-copilot-id="task-status-chip"
            >
              {meta.label}
            </span>
            {task.creditsUsed != null && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground" title="Crédits IA consommés">
                <Coins className="h-3 w-3" />
                {task.creditsUsed} cr
              </span>
            )}
            {task.stepsTotal != null && task.stepsTotal > 0 && (
              <span className="text-[10px] text-muted-foreground">
                {task.stepsDone ?? 0}/{task.stepsTotal} étapes
              </span>
            )}
            <span className="text-[10px] text-muted-foreground/70">{relativeTimeFr(task.createdAt)}</span>
          </div>
          <ProgressBar task={task} />
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {resultRoute && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              title="Ouvrir le résultat"
              data-copilot-id="task-open-result"
              onClick={() => navigate(resultRoute)}
            >
              <ExternalLink className="h-3 w-3" />
            </Button>
          )}
          {task.cancellable && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-red-600"
              title="Annuler la tâche"
              data-copilot-id="task-cancel"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
            </Button>
          )}
        </div>
      </div>

      {cancelFailed && (
        <p className="mt-1.5 text-[10px] text-amber-600 dark:text-amber-400">
          Annulation impossible (backend indisponible) — réessayez plus tard.
        </p>
      )}

      {expanded && hasResultDetail && (
        <div className="mt-2 rounded-md bg-muted/40 p-2" data-copilot-id="task-result-detail">
          {task.error ? (
            <p className="text-[11px] text-red-600 dark:text-red-400">{task.error}</p>
          ) : (
            <pre className="max-h-36 overflow-auto whitespace-pre-wrap break-words text-[10px] text-muted-foreground">
              {typeof task.resultSummary === 'string'
                ? task.resultSummary
                : JSON.stringify(task.resultSummary, null, 2)}
            </pre>
          )}
        </div>
      )}
      {expanded && !hasResultDetail && (
        <p className="mt-2 text-[10px] text-muted-foreground">Aucun résultat disponible pour le moment.</p>
      )}
    </div>
  )
}

// ── Tray panel ────────────────────────────────────────────────────────────────

export interface TaskTrayProps {
  tasks: CompanionTask[]
  source: CompanionTasksResult['source']
  isLoading: boolean
}

export function TaskTray({ tasks, source, isLoading }: TaskTrayProps) {
  const queryClient = useQueryClient()
  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: COMPANION_TASKS_KEY }),
    [queryClient],
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden" data-copilot-id="companion-task-tray">
      <div className="flex shrink-0 items-center justify-between border-b px-3 py-2">
        <span className="flex items-center gap-1.5 text-xs font-medium">
          <ListTodo className="h-3.5 w-3.5 text-primary" />
          Mes tâches IA
          {tasks.length > 0 && (
            <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">
              {tasks.length}
            </Badge>
          )}
        </span>
        <Button variant="ghost" size="icon" className="h-6 w-6" title="Rafraîchir" onClick={refresh} data-copilot-id="task-tray-refresh">
          <RefreshCw className={cn('h-3 w-3', isLoading && 'animate-spin')} />
        </Button>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-2 p-3">
          {isLoading && tasks.length === 0 && (
            <div className="flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Chargement des tâches…
            </div>
          )}

          {!isLoading && tasks.length === 0 && source === 'none' && (
            <div className="py-8 text-center" data-copilot-id="task-tray-unavailable">
              <CircleSlash className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
              <p className="text-xs font-medium">Suivi des tâches bientôt disponible</p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Le service de suivi n'est pas encore déployé sur cet environnement. Vos
                tâches lancées en mode Agent apparaîtront ici automatiquement.
              </p>
            </div>
          )}

          {!isLoading && tasks.length === 0 && source !== 'none' && (
            <div className="py-8 text-center" data-copilot-id="task-tray-empty">
              <ListTodo className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
              <p className="text-xs font-medium">Aucune tâche pour le moment</p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Lancez une action en mode Agent — sa progression, son statut et son coût
                en crédits s'afficheront ici.
              </p>
            </div>
          )}

          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} onCancelled={refresh} />
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

export default TaskTray
